import React from 'react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import {
  type FieldExtensionComponentProps,
  type FieldSchema,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';

import { TessaUserLicensePickerComponent } from './tessa-user-licenses-picker';
import type { TessaUserLicense } from './tessa-user-licenses-query';
import {
  TESSA_USER_LICENSE_PICKER,
  userLicenseValidator,
} from './tessa-user-licenses-validation';

/**
 * Renders the picker only for an `object` schema.
 *
 * @remarks
 * A template can name this field on a property of another type, where an
 * `{ id, name }` value would be wrong. Delegating that case to Backstage's
 * `SchemaField`, without this field's selector, keeps the framework fallback
 * and does not select the picker again.
 */
function ObjectOnlyPicker(
  props: FieldExtensionComponentProps<TessaUserLicense>,
): React.ReactElement {
  if (props.schema.type === 'object') {
    return React.createElement(TessaUserLicensePickerComponent, props);
  }
  const { ['ui:field']: _ownField, ...uiSchema } = props.uiSchema;
  const SchemaField = props.registry.fields.SchemaField;
  if (SchemaField === undefined) {
    throw new Error(
      'TessaUserLicensePicker fallback needs the RJSF SchemaField in the registry',
    );
  }
  return React.createElement(SchemaField, { ...props, uiSchema });
}

/** Closed schema that accepts no author supplied `ui:options`. */
export const tessaUserLicensePickerSchema: FieldSchema<
  TessaUserLicense,
  undefined
> = makeFieldSchema({
  output: z => z.custom<TessaUserLicense>(),
  uiOptions: z => z.never().optional(),
});

/** Form field registration for the signed-in user's Tessa licenses. */
export const TessaUserLicensePicker: FormField = createFormField({
  component: ObjectOnlyPicker,
  name: TESSA_USER_LICENSE_PICKER,
  schema: tessaUserLicensePickerSchema,
  validation: userLicenseValidator,
});

export const tessaUserLicensePickerExtension: ExtensionDefinition<{
  kind: 'scaffolder-form-field';
  name: 'user-license-picker';
  params: { field: () => Promise<FormField> };
}> = FormFieldBlueprint.make({
  name: 'user-license-picker',
  params: { field: async () => TessaUserLicensePicker },
});
